import { useTranslation } from 'react-i18next';
import type { TimelineShot } from '@/types/timeline.ts';
import type { TransitionAtTime } from '@/store/projectStore.ts';
import { capabilities } from '@/capabilities.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { APPROVAL_COLORS } from '@/export/playerConstants.ts';
import styles from './Viewer.module.css';

// ── Aspect ratio parsing ──────────────────────────────────────────────
export { parseAspectRatio } from '@/utils/aspectRatio.ts';

// ── Content rect calculation ──────────────────────────────────────────
export function computeContentRect(
  containerW: number, containerH: number,
  naturalW: number, naturalH: number,
) {
  if (naturalW === 0 || naturalH === 0) {
    return { x: 0, y: 0, width: containerW, height: containerH };
  }
  const scale = Math.min(containerW / naturalW, containerH / naturalH);
  const w = naturalW * scale;
  const h = naturalH * scale;
  return { x: (containerW - w) / 2, y: (containerH - h) / 2, width: w, height: h };
}

// ── The frame at the playhead (shared by Viewer and TimelinePlaybackMirror) ──
/**
 * 20260929 - WHAT THE BIG PLAYBACK PICTURE SHOWS AT THIS INSTANT, as pure data, so the Viewer and its
 * passive mirror (TimelinePlaybackMirror, the laptop pose's top half) derive it ONE way and cannot drift.
 * Hooks stay in each component; this is only the arithmetic both of them used to spell out inline.
 */
export interface ViewerFrame {
  /** The transition at the playhead, kept only when it involves the shot at the playhead. */
  transInfo: TransitionAtTime | null;
  /** The same transition when it is a DISSOLVE (a legacy 'continuous' one with no video included),
   *  else null — the dissolve paints its incoming shot over the outgoing one. */
  dissolve: TransitionAtTime | null;
  /** A fade to or from black: the half that is showing and its opacity. Null for anything else. */
  fade: { shot: TimelineShot; opacity: number } | null;
  /** The shot whose media the big picture shows: the dissolve's outgoing shot, else the playhead's. */
  primaryShot: TimelineShot | null;
  /** The primary picture's opacity: 1 - progress through a dissolve, else 1. */
  primaryOpacity: number;
  /** The shot the full-res still is requested for (follows primaryShot). */
  fullResShot: TimelineShot | null;
  /** The shot the primary video decoder syncs to: a transition's outgoing shot, else the playhead's
   *  (locked so the decoders do not swap at the progress midpoint). */
  videoSyncShot: TimelineShot | null;
}

export function deriveViewerFrame(shot: TimelineShot | null, rawTransInfo: TransitionAtTime | null): ViewerFrame {
  const transInfo = rawTransInfo && shot
    && (shot.id === rawTransInfo.fromShot.id || shot.id === rawTransInfo.toShot.id)
    ? rawTransInfo : null;
  const isDissolve = transInfo !== null && (transInfo.transition.type === 'dissolve'
    || (transInfo.transition.type === 'continuous' && !transInfo.transition.videoObjectUrl));
  const dissolve = isDissolve ? transInfo : null;

  let fade: ViewerFrame['fade'] = null;
  if (transInfo !== null
    && (transInfo.transition.type === 'fade_to_black' || transInfo.transition.type === 'fade_from_black')) {
    const isFirstHalf = transInfo.progress < 0.5;
    fade = {
      shot: isFirstHalf ? transInfo.fromShot : transInfo.toShot,
      opacity: isFirstHalf ? 1 - transInfo.progress * 2 : (transInfo.progress - 0.5) * 2,
    };
  }

  const primaryShot = dissolve ? dissolve.fromShot : shot;
  return {
    transInfo,
    dissolve,
    fade,
    primaryShot,
    primaryOpacity: dissolve ? 1 - dissolve.progress : 1,
    fullResShot: primaryShot,
    videoSyncShot: transInfo ? transInfo.fromShot : shot,
  };
}

/** 20260929 - The still the big picture shows: the AI input-image toggle (an explicit user override)
 *  over the full-res frame (null until Unity answers) over the cache thumbnail. */
export function pickDisplayThumbnail(
  inputUrl: string | null | undefined,
  fullResUrl: string | null | undefined,
  thumbnailDataUrl: string | null | undefined,
): string | null {
  return inputUrl ?? fullResUrl ?? thumbnailDataUrl ?? null;
}

// ── Render helper for video/thumbnail/placeholder in transitions ────
export function renderShotMedia(
  shot: TimelineShot,
  sceneColor: string,
  opacity: number,
  cRef: React.RefObject<HTMLCanvasElement | null>,
  inputImageUrl?: string | null,
) {
  if (shot.videoObjectUrl) {
    return (
      <canvas
        ref={cRef}
        className={styles.transitionCanvas}
        style={{ opacity }}
      />
    );
  }

  const thumbnail = inputImageUrl ?? shot.thumbnailDataUrl;

  if (thumbnail) {
    return (
      <img
        className={styles.transitionLayer}
        src={thumbnail}
        alt=""
        style={{ opacity }}
      />
    );
  }

  return (
    <div
      className={styles.transitionLayer}
      style={{
        opacity,
        background: `linear-gradient(135deg, ${sceneColor} 0%, ${sceneColor}88 100%)`,
      }}
    />
  );
}

// ── Shot label overlay ──────────────────────────────────────────────
interface ShotLabelProps {
  heading: string;
  shot: { number: string; focalLength: number; fStop: number; shotSize?: string; shotAngle?: string; shotMovement?: string };
  metaLine: string | null;
}

export function ShotLabel({ heading, shot, metaLine }: ShotLabelProps) {
  const { t } = useTranslation('viewer');
  return (
    <div className={styles.shotLabel}>
      <span>{heading}</span>
      <span>{t('shotLabelPrefix', { number: shot.number })}</span>
      <span>{shot.focalLength}mm f/{shot.fStop}</span>
      {metaLine && <span className={styles.metaLabel}>{metaLine}</span>}
    </div>
  );
}

// ── Fullscreen button (bottom-right — fallback for transitions/empty) ─
export function FullscreenBtn({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  const { t } = useTranslation('viewer');
  return (
    <button
      className={styles.fullscreenBtn}
      onClick={onClick}
      disabled={disabled}
      title={t('fullscreenToggleTooltip')}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15,3 21,3 21,9" />
        <polyline points="9,21 3,21 3,15" />
        <line x1="21" y1="3" x2="14" y2="10" />
        <line x1="3" y1="21" x2="10" y2="14" />
      </svg>
    </button>
  );
}

// ── Review overlay (top-right) ──────────────────────────────────────
interface ReviewOverlayProps {
  approval: { status: string } | null;
  commentCount: number;
  onToggle: () => void;
}

export function ReviewOverlay({ approval, commentCount, onToggle }: ReviewOverlayProps) {
  const { t } = useTranslation('viewer');
  if (!capabilities.comments) return null;
  const commentPanelOpen = useUiStore(s => s.commentPanelOpen);
  if (commentPanelOpen) return null;
  const status = approval?.status ?? 'pending';
  return (
    <div className={styles.reviewOverlay}>
      <span
        className={styles.approvalPill}
        style={{ background: APPROVAL_COLORS[status] ?? '#888', cursor: 'pointer' }}
        onClick={onToggle}
        title={t('toggleCommentsTooltip')}
      >
        {status.replace('_', ' ')}
      </span>
      <button className={styles.commentBtn} onClick={onToggle} title={t('toggleCommentsTooltip')}>
        {commentCount > 0 ? commentCount : '\u{1F4AC}'}
      </button>
    </div>
  );
}
